"use client";

import { Box, Button, Center, Spinner } from "@chakra-ui/react";
import { useCallback, useRef, useState } from "react";

type HtmxWrapperProps = {
  route: string;
  /** htmx extensions to load (e.g., "preload,class-tools,response-targets,sse") */
  extensions?: string;
};

const ERROR_MESSAGE = "Couldn't load this content.";

/**
 * A wrapper component for htmx-powered pages that shows a loading spinner
 * until the htmx content is loaded and swapped in, or an error if it fails.
 *
 * Self-initializes via a ref callback — calls htmx.process() on its own
 * element so the hx-trigger="load" fires exactly once. Listeners register
 * here too, before process(), since hx-trigger="load" fires during it. This
 * relies on reactStrictMode: false (next.config.mjs) — move both into a
 * useEffect with cleanup if that's ever enabled.
 */
export function HtmxWrapper({
  route,
  extensions = "preload,class-tools,response-targets",
}: HtmxWrapperProps) {
  const [status, setStatus] = useState<"idle" | "failed" | "swapped">("idle");
  const elRef = useRef<HTMLDivElement | null>(null);

  const processRef = useCallback((el: HTMLDivElement | null) => {
    elRef.current = el;
    if (el) {
      const onError = (e: Event) => {
        if (e.target === el) setStatus("failed");
      };
      const onSwap = (e: Event) => {
        if (e.target === el) setStatus("swapped");
      };
      el.addEventListener("htmx:responseError", onError);
      el.addEventListener("htmx:sendError", onError);
      el.addEventListener("htmx:timeout", onError);
      el.addEventListener("htmx:afterSwap", onSwap);
      (window as any).htmx?.process(el);
    }
  }, []);

  const retry = useCallback(() => {
    const el = elRef.current;
    if (!el) return;
    setStatus("idle");
    (window as any).htmx?.ajax("GET", route, { source: el, target: el, swap: "innerHTML" });
  }, [route]);

  return (
    <Box h="100%" w="100%" position="relative">
      <div
        ref={processRef}
        className="virtuous"
        hx-get={route}
        hx-trigger="load"
        hx-swap="innerHTML"
        style={{ height: "100%", width: "100%" }}
        hx-ext={extensions}
        suppressHydrationWarning
      />
      {status !== "swapped" && (
        <Center position="absolute" inset={0}>
          {status === "failed" ? (
            <Box role="alert">
              {ERROR_MESSAGE}
              <Button size="sm" ml={2} onClick={retry}>
                Retry
              </Button>
            </Box>
          ) : (
            <Spinner size="xl" color="blue.500" thickness="4px" />
          )}
        </Center>
      )}
    </Box>
  );
}
